import InboxIcon from "@redotech/redo-icons/inbox-01.svg";
import MessageSquareIcon from "@redotech/redo-icons/message-text-square-02.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import ShieldDollarIcon from "@redotech/redo-icons/shield-dollar.svg";
import { ComponentType, SVGProps } from "react";
import { WalkthroughDefinition } from "./walkthrough-types";

/**
 * Registers a walkthrough by id. The `loader` lazy-imports the step
 * definition; the optional asset loaders lazy-import per-product UI and
 * data sources used while the walkthrough is active. The engine awaits
 * all loaders in parallel before activating, so consumer pages no longer
 * need to wire their own dynamic imports per product.
 */
export interface WalkthroughRegistryEntry {
  id: string;
  name: string;
  Icon?: ComponentType<SVGProps<SVGSVGElement>>;
  loader: () => Promise<{ definition: WalkthroughDefinition }>;
  /** Loads a product-specific data source (e.g. a mock TableFetcher). */
  mockSourceLoader?: () => Promise<{ source: unknown }>;
  /** Loads a product-specific overlay component to swap into the page. */
  overlayLoader?: () => Promise<{ Component: ComponentType<any> }>;
  /** Hide from the account-menu picker (e.g. auto-start-only, page-specific tours). */
  hideFromPicker?: boolean;
}

export const WALKTHROUGH_REGISTRY: WalkthroughRegistryEntry[] = [
  {
    id: "support-v2-list",
    name: "Support inbox",
    Icon: InboxIcon,
    loader: () => import("../support-v2/walkthrough/list-definition"),
    hideFromPicker: true,
  },
  {
    id: "support-v2-ticket",
    name: "Support ticket",
    Icon: MessageSquareIcon,
    loader: () => import("../support-v2/walkthrough/ticket-definition"),
    hideFromPicker: true,
  },
  {
    id: "returns",
    name: "Returns",
    Icon: PackageIcon,
    loader: () => import("../return/walkthrough/definition"),
    mockSourceLoader: () =>
      import("../return/walkthrough/mock-return-fetcher").then((m) => ({
        source: m.mockReturnFetcher,
      })),
    overlayLoader: () =>
      import("../return/walkthrough/walkthrough-return-detail").then((m) => ({
        Component: m.WalkthroughReturnDetail,
      })),
  },
  {
    id: "disputes",
    name: "Disputes",
    Icon: ShieldDollarIcon,
    loader: () => import("../payments/chargebacks/walkthrough/definition"),
    mockSourceLoader: () =>
      import("../payments/chargebacks/walkthrough/mock-source").then((m) => ({
        source: m.disputesWalkthroughSource,
      })),
  },
];
